/**
 * Orchestration de l'extraction.
 *
 * PDF  → (Worker) pièces jointes + XMP → (thread principal) DOMParser → modèle
 * XML  → (thread principal) DOMParser → modèle
 *
 * Le découpage suit une contrainte plateforme : DOMParser n'existe pas dans un
 * Worker, pdf.js y est en revanche à sa place. Le résultat porte toujours ses
 * avertissements : l'UI (tâche 2) n'a jamais à deviner si une valeur est
 * absente ou si le document est douteux.
 */
import {
  type ExtractionResult,
  type ExtractionWarning,
  type InvoiceDocument,
  type PdfMeta,
  type WarningSeverity,
} from './model';
import { parseInvoiceXml, XmlParseError } from './xml';
import { collectPdfAttachments, pickInvoiceXml, type PdfAttachment, type PdfDocLike } from './pdf/attachments';
import { profileFromXmpLevel } from './profile';
import { sum, withinTolerance } from './money';

export interface ExtractOptions {
  /** Nom du fichier d'origine (diagnostic uniquement). */
  filename?: string;
}

function w(code: string, message: string, severity: WarningSeverity = 'warn'): ExtractionWarning {
  return { code, message, severity };
}

/** Contrôles de cohérence arithmétique, non bloquants. */
export function checkTotals(doc: InvoiceDocument): ExtractionWarning[] {
  const out: ExtractionWarning[] = [];
  const { taxBasis, taxTotal, grandTotal, lineNet } = doc.totals;

  if (taxBasis && taxTotal && grandTotal && !withinTolerance(taxBasis.plus(taxTotal), grandTotal, '0.02')) {
    out.push(
      w(
        'TOTALS_INCONSISTENT',
        `Total TTC (${grandTotal.toFixed(2)}) ≠ HT (${taxBasis.toFixed(2)}) + TVA (${taxTotal.toFixed(2)})`,
      ),
    );
  }

  if (taxTotal && doc.taxes.length > 0) {
    const declared = doc.taxes.map((t) => t.taxAmount);
    if (declared.some(Boolean)) {
      const computed = sum(declared);
      if (!withinTolerance(computed, taxTotal, '0.02')) {
        out.push(
          w(
            'TOTALS_INCONSISTENT',
            `Somme des TVA par taux (${computed.toFixed(2)}) ≠ TVA totale (${taxTotal.toFixed(2)})`,
          ),
        );
      }
    }
  }

  if (lineNet && doc.lines.length > 0) {
    const lines = doc.lines.map((l) => l.lineNetAmount);
    if (lines.every(Boolean)) {
      const computed = sum(lines);
      if (!withinTolerance(computed, lineNet, '0.05')) {
        out.push(
          w('TOTALS_INCONSISTENT', `Somme des lignes (${computed.toFixed(2)}) ≠ total des lignes (${lineNet.toFixed(2)})`),
        );
      }
    }
  }
  return out;
}

/** Contrôle croisé XMP ↔ BT-24. */
export function checkXmpProfile(doc: InvoiceDocument, xmpLevel: string | null): ExtractionWarning[] {
  const xmpProfile = profileFromXmpLevel(xmpLevel);
  if (!xmpProfile || doc.profile.profile === 'UNKNOWN') return [];
  if (xmpProfile !== doc.profile.profile && !(doc.profile.profile === 'XRECHNUNG' && xmpProfile === 'EN16931')) {
    return [
      w(
        'PROFILE_MISMATCH',
        `XMP annonce « ${xmpLevel} » mais le BT-24 déclare « ${doc.profile.profile} » (le XML fait foi)`,
      ),
    ];
  }
  return [];
}

export interface PdfExtractionInput {
  attachments: PdfAttachment[];
  meta: PdfMeta;
  warnings?: ExtractionWarning[];
}

/**
 * Étape finale, indépendante de pdf.js : choisit le XML, l'interprète et
 * assemble le résultat. Utilisée par le Worker (via le client) et par les tests.
 */
export function finalizePdfExtraction(input: PdfExtractionInput, options: ExtractOptions = {}): ExtractionResult {
  const selection = pickInvoiceXml(input.attachments);
  const warnings: ExtractionWarning[] = [...(input.warnings ?? []), ...selection.warnings];

  if (!selection.chosen) {
    throw new XmlParseError(
      input.attachments.length === 0
        ? 'Ce PDF ne contient aucune pièce jointe : aucune facture électronique à lire'
        : 'Aucune facture XML reconnue dans les pièces jointes de ce PDF',
      input.attachments.length === 0 ? 'NO_AF_ATTACHMENT' : 'NO_XML_ATTACHMENT',
    );
  }

  const attachment = selection.chosen;
  const outcome = parseInvoiceXml(attachment.bytes);
  warnings.push(...outcome.warnings);
  warnings.push(...checkXmpProfile(outcome.doc, input.meta.xmpProfileLevel));
  warnings.push(...checkTotals(outcome.doc));

  if (input.meta.xmpDocumentFileName && input.meta.xmpDocumentFileName !== attachment.filename) {
    warnings.push(
      w(
        'PROFILE_MISMATCH',
        `XMP désigne « ${input.meta.xmpDocumentFileName} », pièce jointe retenue « ${attachment.filename} »`,
        'info',
      ),
    );
  }

  const meta: PdfMeta = {
    ...input.meta,
    xmlSource: attachment.source === 'name-tree' ? 'name-tree' : 'af',
    xmlFilename: attachment.filename,
  };

  return {
    doc: outcome.doc,
    source: {
      kind: 'pdf',
      filename: options.filename ?? attachment.filename,
      byteLength: attachment.bytes.length,
    },
    pdf: meta,
    warnings,
  };
}

/** PDF déjà ouvert par pdf.js (tests, ou usage direct hors Worker). */
export async function extractFromPdfDoc(pdf: PdfDocLike, options: ExtractOptions = {}): Promise<ExtractionResult> {
  const collected = await collectPdfAttachments(pdf);
  return finalizePdfExtraction(collected, options);
}

/** Fichier XML autonome (UBL 2.1 ou CII). */
export function extractFromXmlBytes(bytes: Uint8Array, options: ExtractOptions = {}): ExtractionResult {
  const outcome = parseInvoiceXml(bytes);
  const warnings = [...outcome.warnings, ...checkTotals(outcome.doc)];
  return {
    doc: outcome.doc,
    source: { kind: 'xml', filename: options.filename ?? null, byteLength: bytes.length },
    pdf: null,
    warnings,
  };
}

/** Détection binaire : %PDF- vs XML. */
export function looksLikePdf(bytes: Uint8Array): boolean {
  if (bytes.length < 5) return false;
  const head = new TextDecoder('latin1').decode(bytes.subarray(0, 5));
  return head === '%PDF-';
}
